/**
 * Inventory Review Modal Module
 * Shows inventory actions preview before finalizing service
 *
 * Displays:
 * - Anodes to be replaced (deducted from inventory)
 * - Anodes to be retrieved (reserved for next service + packing task)
 * - Anodes to be ordered (added to replenishment list)
 * - Stock warnings for insufficient inventory
 */

import { supabase } from '../supabase/client.js';

/**
 * Refresh inventory status for all anodes
 * Uses global window.fetchAnodeInventoryStatus from assignment-cards.js
 * @param {Object[]} anodeAssignments - Array of anode assignments
 * @param {string} boatId - The boat ID to fetch inventory status for
 * @returns {Promise<void>}
 */
async function refreshInventoryStatus(anodeAssignments, boatId) {
    if (!boatId) {
        console.warn('No boat ID available for inventory refresh');
        return;
    }

    // Extract anode catalog IDs from assignments
    // Use anode_catalog_id (edge function format) with fallback to anode_id for safety
    const anodeCatalogIds = anodeAssignments
        .map(a => a.anode_catalog_id || a.anode_id)
        .filter(Boolean);

    if (anodeCatalogIds.length === 0) {
        console.warn('No anode catalog IDs to fetch inventory status for');
        return;
    }

    // Call the global inventory status function
    if (typeof window.fetchAnodeInventoryStatus === 'function') {
        console.log(`🔄 Refreshing inventory status for ${anodeCatalogIds.length} anodes`);
        const inventoryStatuses = await window.fetchAnodeInventoryStatus(anodeCatalogIds, boatId);
        window.__anodeInventoryStatus = inventoryStatuses;
        console.log('✅ Inventory status refreshed:', inventoryStatuses);
    } else {
        console.warn('⚠️ window.fetchAnodeInventoryStatus not available');
    }
}

/**
 * Build inventory summary section showing counts by action
 * @param {Object[]} anodeAssignments - Array of anode assignments
 * @returns {string} HTML for summary section
 */
function buildInventorySummary(anodeAssignments) {
    let toReplace = 0;
    let toRetrieve = 0;
    let toOrder = 0;

    anodeAssignments.forEach(anode => {
        const status = anode.status;
        if (status === 'replaced' && anode.anode_id) toReplace++;
        else if (status === 'retrieve' && anode.anode_id) toRetrieve++;
        else if (status === 'order' && anode.anode_id) toOrder++;
    });

    if (toReplace === 0 && toRetrieve === 0 && toOrder === 0) {
        return '';
    }

    return `
        <div class="inventory-summary-section">
            <h4 style="margin: 0 0 12px 0; color: #333; font-size: 16px;">📊 Inventory Actions Summary</h4>
            <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                ${toReplace > 0 ? `
                    <div class="inventory-summary-item">
                        <div class="inventory-summary-count">${toReplace}</div>
                        <div class="inventory-summary-label">To Replace</div>
                        <div class="inventory-summary-description">Will consume inventory</div>
                    </div>
                ` : ''}
                ${toRetrieve > 0 ? `
                    <div class="inventory-summary-item">
                        <div class="inventory-summary-count">${toRetrieve}</div>
                        <div class="inventory-summary-label">To Retrieve</div>
                        <div class="inventory-summary-description">Added to packing list</div>
                    </div>
                ` : ''}
                ${toOrder > 0 ? `
                    <div class="inventory-summary-item">
                        <div class="inventory-summary-count">${toOrder}</div>
                        <div class="inventory-summary-label">To Order</div>
                        <div class="inventory-summary-description">Added to reorder queue</div>
                    </div>
                ` : ''}
            </div>
        </div>
    `;
}

/**
 * Open inventory review modal
 * @param {Object[]} anodeAssignments - Array of anode assignments from window.anodeAssignments
 * @param {Function} onConfirm - Callback function when user confirms
 * @returns {Promise<void>}
 */
export async function openInventoryReviewModal(anodeAssignments, onConfirm) {
    // Remove existing modal if present
    closeInventoryReviewModal();

    // Show loading modal first
    const loadingHtml = `
        <div class="modal-overlay" id="inventoryReviewModal">
            <div class="inventory-modal-content">
                <div class="inventory-modal-header">
                    <h3>Loading Inventory Data...</h3>
                </div>
                <div class="inventory-modal-body" style="text-align: center; padding: 40px;">
                    <div style="font-size: 18px; color: #666;">⏳ Checking inventory levels...</div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', loadingHtml);

    // Get boat ID from global state
    const boatId = window.currentBoatId || window.selectedBoat?.id;

    if (!boatId) {
        closeInventoryReviewModal();
        alert('Unable to determine boat ID. Please refresh the page and try again.');
        return;
    }

    // Refresh inventory status to get latest stock levels
    await refreshInventoryStatus(anodeAssignments, boatId);

    // Group anodes by status
    const replacedAnodes = anodeAssignments.filter(a => a.status === 'replaced' && a.anode_id);
    const retrieveAnodes = anodeAssignments.filter(a => a.status === 'retrieve' && a.anode_id);
    const orderAnodes = anodeAssignments.filter(a => a.status === 'order' && a.anode_id);

    // Fetch inventory data for replaced and retrieve anodes
    const inventoryData = await fetchInventoryLevels([...replacedAnodes, ...retrieveAnodes]);

    // Attach inventory IDs to anode assignments for downstream processing
    [...replacedAnodes, ...retrieveAnodes].forEach(anode => {
        const inv = inventoryData.get(anode.anode_id);
        if (inv && inv.id) {
            anode.inventory_id = inv.id;
        }
    });

    // Build preview sections
    const inventorySummary = buildInventorySummary(anodeAssignments);
    const replacedSection = buildReplacedSection(replacedAnodes, inventoryData);
    const retrieveSection = buildRetrieveSection(retrieveAnodes, inventoryData);
    const orderSection = buildOrderSection(orderAnodes, inventoryData);
    const warningsSection = buildWarningsSection(inventoryData);

    // Check if there are any actions to review
    const hasActions = replacedAnodes.length > 0 || retrieveAnodes.length > 0 || orderAnodes.length > 0;

    if (!hasActions) {
        closeInventoryReviewModal();
        alert('No inventory actions to process. Please mark at least one anode status.');
        return;
    }

    // Remove loading modal
    document.getElementById('inventoryReviewModal')?.remove();

    // Create main modal
    const modalHtml = `
        <div class="modal-overlay" id="inventoryReviewModal" onclick="window.__closeInventoryReviewModal(event)">
            <div class="inventory-modal-content" onclick="event.stopPropagation()" style="max-width: 800px;">
                <div class="inventory-modal-header">
                    <h3>Review Inventory Actions</h3>
                    <button onclick="window.__closeInventoryReviewModal()" class="inventory-modal-close">&times;</button>
                </div>
                <div class="inventory-modal-body">
                    <p style="margin-bottom: 24px; color: #666; font-size: 15px;">
                        Review the inventory changes that will be applied when you finalize this service:
                    </p>

                    ${inventorySummary}
                    ${warningsSection}
                    ${replacedSection}
                    ${retrieveSection}
                    ${orderSection}

                    <div style="margin-top: 32px; padding-top: 24px; border-top: 2px solid #e0e0e0;">
                        <div style="display: flex; gap: 12px; justify-content: flex-end;">
                            <button onclick="window.__closeInventoryReviewModal()"
                                    class="inventory-btn inventory-btn-cancel">
                                Cancel
                            </button>
                            <button onclick="window.__confirmInventoryActions()"
                                    class="inventory-btn inventory-btn-confirm">
                                Confirm & Finalize Service
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Store callback for later use
    window.__inventoryConfirmCallback = onConfirm;
}

/**
 * Close the inventory review modal
 * @param {Event} event - Optional click event
 */
export function closeInventoryReviewModal(event) {
    if (event && event.target !== event.currentTarget) return;
    const modal = document.getElementById('inventoryReviewModal');
    if (modal) modal.remove();
    delete window.__inventoryConfirmCallback;
}

/**
 * Confirm inventory actions and trigger callback
 */
function confirmInventoryActions() {
    if (window.__inventoryConfirmCallback) {
        window.__inventoryConfirmCallback();
    }
    closeInventoryReviewModal();
}

/**
 * Fetch inventory levels for given anodes
 * @param {Object[]} anodes - Array of anode objects
 * @returns {Promise<Map>} Map of anode_id to inventory data
 */
async function fetchInventoryLevels(anodes) {
    if (anodes.length === 0) return new Map();

    const anodeIds = anodes.map(a => a.anode_id);

    try {
        const { data, error } = await supabase
            .from('anode_inventory')
            .select('id, anode_id, quantity_on_hand, quantity_allocated, quantity_available, primary_location, bin_number')
            .in('anode_id', anodeIds);

        if (error) throw error;

        // Create map for quick lookup
        const inventoryMap = new Map();
        data?.forEach(inv => {
            inventoryMap.set(inv.anode_id, inv);
        });

        return inventoryMap;
    } catch (error) {
        console.error('Error fetching inventory:', error);
        return new Map();
    }
}

/**
 * Build replaced anodes section
 */
function buildReplacedSection(anodes, inventoryData) {
    if (anodes.length === 0) return '';

    const items = anodes.map(anode => {
        const inv = inventoryData.get(anode.anode_id);
        const stockInfo = inv ? `(${inv.quantity_on_hand} in stock)` : '(stock unknown)';
        const stockClass = inv && inv.quantity_on_hand > 0 ? 'stock-ok' : 'stock-warning';

        return `
            <div class="inventory-item">
                <div class="inventory-item-icon">✅</div>
                <div class="inventory-item-details">
                    <div class="inventory-item-name">${anode.anode_name}</div>
                    <div class="inventory-item-action">Will be deducted from inventory</div>
                </div>
                <div class="inventory-item-stock ${stockClass}">${stockInfo}</div>
            </div>
        `;
    }).join('');

    return `
        <div class="inventory-section">
            <div class="inventory-section-header">
                <h4>✅ Anodes to Replace (${anodes.length})</h4>
                <p>These anodes will be deducted from inventory and charged to customer</p>
            </div>
            <div class="inventory-items-list">
                ${items}
            </div>
        </div>
    `;
}

/**
 * Build retrieve anodes section
 */
function buildRetrieveSection(anodes, inventoryData) {
    if (anodes.length === 0) return '';

    const items = anodes.map(anode => {
        const inv = inventoryData.get(anode.anode_id);
        const location = inv?.primary_location || 'storage';
        const bin = inv?.bin_number ? `, Bin ${inv.bin_number}` : '';

        // Show both on_hand and available for clarity
        const stockInfo = inv
            ? `(${inv.quantity_on_hand} in stock, ${inv.quantity_available} available)`
            : '(stock unknown)';
        const stockClass = inv && inv.quantity_on_hand > 0 ? 'stock-ok' : 'stock-warning';
        const willReorder = inv && inv.quantity_available < 1;

        return `
            <div class="inventory-item">
                <div class="inventory-item-icon">🔄</div>
                <div class="inventory-item-details">
                    <div class="inventory-item-name">${anode.anode_name}</div>
                    <div class="inventory-item-action">Will reserve stock & create retrieval task</div>
                    <div class="inventory-item-location">📍 ${location}${bin}</div>
                    ${willReorder ? '<div class="inventory-item-reorder">⚠️ Will automatically add to reorder list (insufficient stock)</div>' : ''}
                </div>
                <div class="inventory-item-stock ${stockClass}">${stockInfo}</div>
            </div>
        `;
    }).join('');

    return `
        <div class="inventory-section">
            <div class="inventory-section-header">
                <h4>🔄 Anodes to Retrieve (${anodes.length})</h4>
                <p>These anodes will be reserved and appear in Operations packing lists for retrieval before next service</p>
            </div>
            <div class="inventory-items-list">
                ${items}
            </div>
        </div>
    `;
}

/**
 * Build order anodes section
 */
function buildOrderSection(anodes, inventoryData) {
    if (anodes.length === 0) return '';

    const items = anodes.map(anode => {
        const inv = inventoryData.get(anode.anode_id);
        // Show actual stock level even for "order" items
        const stockInfo = inv
            ? `(${inv.quantity_on_hand} in stock - need to order more)`
            : '(stock unknown - will order)';
        const stockClass = 'stock-warning';

        return `
            <div class="inventory-item">
                <div class="inventory-item-icon">📦</div>
                <div class="inventory-item-details">
                    <div class="inventory-item-name">${anode.anode_name}</div>
                    <div class="inventory-item-action">Will be added to reorder list (high priority)</div>
                </div>
                <div class="inventory-item-stock ${stockClass}">${stockInfo}</div>
            </div>
        `;
    }).join('');

    return `
        <div class="inventory-section">
            <div class="inventory-section-header">
                <h4>📦 Anodes to Order (${anodes.length})</h4>
                <p>These anodes will be added to the replenishment list with high priority</p>
            </div>
            <div class="inventory-items-list">
                ${items}
            </div>
        </div>
    `;
}

/**
 * Build warnings section for low stock
 */
function buildWarningsSection(inventoryData) {
    const warnings = [];
    inventoryData.forEach((inv, anodeId) => {
        if (inv.quantity_on_hand < 3) {
            warnings.push(`Low stock alert: Only ${inv.quantity_on_hand} units available`);
        }
    });

    if (warnings.length === 0) return '';

    return `
        <div class="inventory-warning-section">
            <div class="inventory-warning-icon">⚠️</div>
            <div>
                <div style="font-weight: 600; margin-bottom: 8px;">Stock Warnings</div>
                ${warnings.map(w => `<div>• ${w}</div>`).join('')}
            </div>
        </div>
    `;
}

// Make functions globally available
window.__closeInventoryReviewModal = closeInventoryReviewModal;
window.__confirmInventoryActions = confirmInventoryActions;

export { confirmInventoryActions };
